import { useMemo } from "react";
import { ChevronDown } from "lucide-react";
import { COLOR_FAMILIES, colorFamily } from "@/lib/colors";
import { sizeValue, compareSizes, sizeLabel } from "@/lib/sizes";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuCheckboxItem, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

export const PRICE_RANGES = [
  { key: "u25", label: "Under $25", test: (p) => p < 25 },
  { key: "25-50", label: "$25 – $50", test: (p) => p >= 25 && p < 50 },
  { key: "50-100", label: "$50 – $100", test: (p) => p >= 50 && p < 100 },
  { key: "100-200", label: "$100 – $200", test: (p) => p >= 100 && p < 200 },
  { key: "200p", label: "$200+", test: (p) => p >= 200 },
];

export const matchesPriceRanges = (price, selected) =>
  !selected.length || selected.some((k) => PRICE_RANGES.find((r) => r.key === k)?.test(price));

// Shared size / colour / price filter bar — used on Shop and sale pages.
export const FilterBar = ({ products, sizes, colors, prices, onSizes, onColors, onPrices }) => {
  const availSizes = useMemo(() => {
    // Relevance-scoped size options: kids sizes (ages, small numerics from
    // kids products) appear only when kids products are in view, adult sizes
    // only when adult products are. Junk data (footwear decimals, colours
    // accidentally entered as sizes) is dropped. Ordered: adult numeric →
    // kids numeric → ages → free/other.
    const byValue = new Map();
    const kidsCount = products.filter((p) => (p.collections || []).some((c) => String(c).startsWith("kids"))).length;
    // Kids sizes surface only when the view is substantially kids (kids pages);
    // a few cross-tagged girls' pieces must not pollute Women/Men filters.
    const showKids = products.length > 0 && kidsCount / products.length >= 0.25;
    const showAdult = products.length > 0 && (products.length - kidsCount) / products.length >= 0.25;
    const isAge = (s) => /year|month/i.test(s);
    const isFree = (s) => /free/i.test(s);
    const isXl = (s) => /^[3-8]XL$/i.test(s);
    products.forEach((p) => {
      (p.sizes || []).forEach((sz) => {
        const raw = String(sz).trim();
        const v = sizeValue(raw);
        if (!v) return;
        const key = v.toLowerCase();
        if (byValue.has(key)) return;
        const n = /^\d+$/.test(v) ? Number(v) : null;
        const plausible = isAge(raw) || isFree(raw) || isXl(raw) || (n !== null && n >= 22 && n <= 60);
        if (!plausible) return;
        // Plain numerics 22-36 are kids' ages per the owner's chart; the same
        // number WITH a parenthetical is adult sizing — unless the parenthetical
        // itself is kids sizing (4T/6T/12 YEAR).
        const paren = raw.match(/^\d{2}\s*\((.+)\)$/);
        const kidsType = isAge(raw) || (paren ? /\d\s*T\b|year/i.test(paren[1]) : n !== null && n <= 36);
        byValue.set(key, { value: v, label: isFree(raw) ? "Free Size" : sizeLabel(raw), kids: kidsType, n, raw });
      });
    });
    const ageNum = (raw) => (/month/i.test(raw) ? 0.5 : Number((raw.match(/\d+(\.\d+)?/) || [99])[0]));
    const rank = (e) => (!e.kids && e.n !== null ? 0 : e.kids && e.n !== null ? 1 : isAge(e.raw) ? 2 : 3);
    return Array.from(byValue.values())
      .filter((e) => (e.kids ? showKids : showAdult))
      .sort((a, b) => {
        const r = rank(a) - rank(b);
        if (r) return r;
        if (a.n !== null && b.n !== null) return a.n - b.n;
        if (isAge(a.raw) && isAge(b.raw)) return ageNum(a.raw) - ageNum(b.raw);
        return a.label.localeCompare(b.label);
      })
      .map(({ value, label }) => ({ value, label }));
  }, [products]);

  const availColors = useMemo(() => {
    const s = new Set();
    products.forEach((p) => (p.colors || []).forEach((c) => { const f = colorFamily(c); if (f) s.add(f.key); }));
    return COLOR_FAMILIES.filter((f) => s.has(f.key));
  }, [products]);

  const availPrices = useMemo(
    () => PRICE_RANGES.filter((r) => products.some((p) => r.test(p.price))),
    [products]
  );

  const toggle = (list, v, setter) => setter(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const active = sizes.length + colors.length + prices.length;
  const clear = () => { onSizes([]); onColors([]); onPrices([]); };

  if (!availSizes.length && !availColors.length && availPrices.length < 2) return null;

  const triggerCls = (on) =>
    `flex items-center gap-2 h-10 px-4 border text-[0.7rem] font-mono tracking-[0.14em] uppercase transition-colors ${on ? "border-[#1A1A1A] bg-[#1A1A1A] text-[#FFFFFF]" : "border-[#E5E5DF] hover:border-[#1A1A1A]"}`;

  return (
    <div className="flex flex-wrap items-center gap-3 mb-8" data-testid="attribute-filters">
      {availSizes.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="size-filter-trigger" className={triggerCls(sizes.length > 0)}>
              Size{sizes.length ? ` (${sizes.length})` : ""} <ChevronDown size={14} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto w-52 bg-[#FFFFFF]">
            <DropdownMenuLabel className="overline text-[#6E6E69]">Select sizes</DropdownMenuLabel>
            {availSizes.map((s) => (
              <DropdownMenuCheckboxItem key={s.value} checked={sizes.includes(s.value)} data-testid={`size-option-${s.value}`}
                onCheckedChange={() => toggle(sizes, s.value, onSizes)} onSelect={(e) => e.preventDefault()}
                className="font-mono text-sm">{s.label}</DropdownMenuCheckboxItem>
            ))}          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {availPrices.length > 1 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="price-filter-trigger" className={triggerCls(prices.length > 0)}>
              Price{prices.length ? ` (${prices.length})` : ""} <ChevronDown size={14} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48 bg-[#FFFFFF]">
            <DropdownMenuLabel className="overline text-[#6E6E69]">Price range</DropdownMenuLabel>
            {availPrices.map((r) => (
              <DropdownMenuCheckboxItem key={r.key} checked={prices.includes(r.key)} data-testid={`price-option-${r.key}`}
                onCheckedChange={() => toggle(prices, r.key, onPrices)} onSelect={(e) => e.preventDefault()}
                className="font-mono text-sm">{r.label}</DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {availColors.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="color-filter-trigger" className={triggerCls(colors.length > 0)}>
              Colour{colors.length ? ` (${colors.length})` : ""} <ChevronDown size={14} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto w-48 bg-[#FFFFFF]">
            <DropdownMenuLabel className="overline text-[#6E6E69]">Select colours</DropdownMenuLabel>
            {availColors.map((f) => (
              <DropdownMenuCheckboxItem key={f.key} checked={colors.includes(f.key)} data-testid={`color-option-${f.key}`}
                onCheckedChange={() => toggle(colors, f.key, onColors)} onSelect={(e) => e.preventDefault()}
                className="text-sm">
                <span className="inline-flex items-center gap-2">
                  <span className={`w-3.5 h-3.5 rounded-full border ${f.key === "White" ? "border-[#1A1A1A]/25" : "border-black/15"}`} style={{ background: f.hex }} />
                  {f.key}
                </span>
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {active > 0 && (
        <button onClick={clear} data-testid="clear-attribute-filters"
          className="overline text-[#B84A3B] border-b border-[#B84A3B] pb-0.5 hover:text-[#1A1A1A] hover:border-[#1A1A1A] transition-colors">
          Clear filters
        </button>
      )}
    </div>
  );
};
